import React, { useEffect, useMemo, useRef, useState } from 'react';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const normalizeOptions = (options) => (
  options.map((option) => ({
    value: option?.value == null ? '' : String(option.value),
    label: option?.label || '',
    secondary: option?.secondary || '',
    searchText: option?.searchText || '',
    color: option?.color || option?.Color || ''
  }))
);

const SearchableSelect = ({
  value,
  values,
  onChange,
  options = [],
  placeholder = 'Buscar...',
  emptyText = 'No hay opciones disponibles.',
  noResultsText = 'No se encontraron resultados.',
  disabled = false,
  multiple = false
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const normalizedValue = value == null ? '' : String(value);
  const normalizedValues = useMemo(() => {
    if (!multiple) return [];
    const rawValues = Array.isArray(values) ? values : Array.isArray(value) ? value : [];
    return rawValues
      .map((item) => (item == null ? '' : String(item)))
      .filter(Boolean);
  }, [multiple, value, values]);

  const normalizedValuesSet = useMemo(
    () => new Set(normalizedValues),
    [normalizedValues]
  );

  const selectedOption = useMemo(() => (
    normalizedOptions.find((option) => option.value === normalizedValue) || null
  ), [normalizedOptions, normalizedValue]);

  const selectedOptions = useMemo(() => {
    if (!multiple) return [];
    return normalizedOptions.filter((option) => normalizedValuesSet.has(option.value));
  }, [multiple, normalizedOptions, normalizedValuesSet]);

  useEffect(() => {
    if (multiple) return;
    if (!isOpen) {
      setQuery(selectedOption?.label || '');
    }
  }, [isOpen, multiple, selectedOption]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const filteredOptions = useMemo(() => {
    const term = normalizeText(query);
    if (!term) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => {
      const haystack = `${option.label} ${option.secondary} ${option.searchText}`;
      return normalizeText(haystack).includes(term);
    });
  }, [normalizedOptions, query]);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    if (!multiple && normalizedValue) {
      onChange('');
    }
    setIsOpen(true);
  };

  const handleSelect = (nextValue) => {
    if (multiple) {
      const nextValues = normalizedValuesSet.has(nextValue)
        ? normalizedValues.filter((item) => item !== nextValue)
        : [...normalizedValues, nextValue];

      onChange(nextValues);
      setQuery('');
      setIsOpen(true);
      return;
    }

    onChange(nextValue);
    setIsOpen(false);
  };

  const handleRemove = (nextValue) => {
    if (disabled) return;
    const nextValues = normalizedValues.filter((item) => item !== nextValue);
    onChange(nextValues);
  };

  const openMenu = () => {
    if (disabled) return;
    if (!multiple && selectedOption?.label === query) {
      setQuery('');
    }
    setIsOpen(true);
  };

  const toggleOpen = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    openMenu();
  };

  return (
    <div className={`searchable-select${disabled ? ' disabled' : ''}${multiple ? ' multiple' : ''}`} ref={containerRef}>
      <div className={`searchable-select-input${isOpen ? ' open' : ''}`}>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={openMenu}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        <button type="button" className="searchable-select-toggle" onClick={toggleOpen} disabled={disabled} aria-label="Abrir opciones">
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
        </button>
      </div>

      {multiple && selectedOptions.length > 0 && (
        <div className="searchable-select-tags">
          {selectedOptions.map((option) => {
            const colorStyle = option.color ? { '--searchable-option-color': option.color } : undefined;
            return (
              <button
                key={option.value}
                type="button"
                className={`searchable-select-tag${option.color ? ' with-color' : ''}`}
                style={colorStyle}
                onClick={() => handleRemove(option.value)}
                disabled={disabled}
                title={`Quitar ${option.label}`}
              >
                <span>{option.label}</span>
                <i className="fas fa-times" />
              </button>
            );
          })}
        </div>
      )}

      {isOpen && (
        <div className="searchable-select-menu">
          {normalizedOptions.length === 0 && (
            <div className="searchable-select-empty">{emptyText}</div>
          )}

          {normalizedOptions.length > 0 && filteredOptions.length === 0 && (
            <div className="searchable-select-empty">{noResultsText}</div>
          )}

          {filteredOptions.map((option) => {
            const isSelected = multiple
              ? normalizedValuesSet.has(option.value)
              : option.value === normalizedValue;
            const colorStyle = option.color ? { '--searchable-option-color': option.color } : undefined;

            return (
              <button
                key={option.value}
                type="button"
                className={`searchable-select-option${isSelected ? ' selected' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option.value)}
              >
                <span className="searchable-select-option-content">
                  <span className={`searchable-select-option-label${option.color ? ' with-color' : ''}`} style={colorStyle}>{option.label}</span>
                  {option.secondary && (
                    <span className="searchable-select-option-secondary">{option.secondary}</span>
                  )}
                </span>
                {multiple && (
                  <span className="searchable-select-option-check">
                    <i className={`fas fa-${isSelected ? 'check-circle' : 'circle'}`} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
